import { ArrowLeft, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DemandExampleTemplate,
  useCloneFromAnalysisMutation,
  useCreateTemplateMutation,
  useListTemplatesQuery,
} from '@/services/api/demandExampleTemplateService';

import { TemplateBuilder } from './TemplateBuilder';
import { TemplatesList } from './TemplatesList';

export const TemplatesPage = () => {
  const { demand_example_id } = useParams<{ demand_example_id: string }>();
  const navigate = useNavigate();
  const demandExampleId = demand_example_id!;
  const { data: templates } = useListTemplatesQuery({ demandExampleId });

  const [isCreating, setIsCreating] = useState(false);
  const [editing, setEditing] = useState<DemandExampleTemplate | null>(null);
  const [activeTab, setActiveTab] = useState<'list' | 'builder'>('list');

  const [createTemplate] = useCreateTemplateMutation();
  const [cloneFromAnalysis] = useCloneFromAnalysisMutation();

  const startCreate = () => {
    setIsCreating(true);
    setEditing(null);
    setActiveTab('builder');
  };

  const handleEdit = (tpl: DemandExampleTemplate) => {
    navigate(`/demand-examples/${demandExampleId}/templates/${tpl.id}/edit`);
  };

  const handleBuilderClose = () => {
    setIsCreating(false);
    setEditing(null);
    setActiveTab('list');
  };

  const handleCreateFromAnalysis = async () => {
    try {
      const header = await createTemplate({
        demandExampleId,
        payload: {
          name: `Default ${new Date().toLocaleDateString()}`,
          demand_example: demandExampleId,
        },
      }).unwrap();

      await cloneFromAnalysis({
        demandExampleId,
        templateId: header.id,
      }).unwrap();

      navigate(`/demand-examples/${demandExampleId}/templates/${header.id}`);
    } catch {
      toast.error('Failed to generate template from analysis');
    }
  };

  return (
    <div className="p-4 flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Button
          plain
          onClick={() => navigate(-1)}
          aria-label="Back"
          className="flex items-center gap-1"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <h2 className="text-lg font-semibold">Demand Example Templates</h2>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as 'list' | 'builder')}
        className="flex flex-col flex-1"
      >
        <TabsList className="flex gap-2 mb-2">
          <TabsTrigger value="list">Templates</TabsTrigger>
          {(isCreating || editing) && (
            <TabsTrigger value="builder">Builder</TabsTrigger>
          )}
          <Button
            type="button"
            onClick={startCreate}
            className="ml-auto flex gap-1 text-sm px-2 py-1"
          >
            <Plus className="h-4 w-4" /> New Template
          </Button>
          <Button
            type="button"
            onClick={handleCreateFromAnalysis}
            className="flex gap-1 text-sm px-2 py-1"
            outline
          >
            Auto Template
          </Button>
        </TabsList>
        <TabsContent value="list" className="flex-1 overflow-auto">
          <TemplatesList
            templates={templates}
            demandExampleId={demandExampleId}
            onEdit={handleEdit}
          />
        </TabsContent>
        <TabsContent value="builder" className="flex-1 overflow-auto">
          {(isCreating || editing) && (
            <TemplateBuilder
              demandExampleId={demandExampleId}
              template={editing}
              onClose={handleBuilderClose}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
